import { useEffect, useRef, useState, type KeyboardEvent } from 'react';

import * as Styled from './SortOptionsListbox.styled';

type SortOption = {
  name: string;
  displayName: string;
  selected: boolean;
};

type Props = {
  options: SortOption[];
  onSelect: (name: string) => void;
};

const SortOptionsListbox = ({ options, onSelect }: Props) => {
  const selectedIndex = options.findIndex((option) => option.selected);
  const initialActiveIndex = Math.max(selectedIndex, 0);

  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

  useEffect(() => {
    optionRefs.current[initialActiveIndex]?.focus();
    // Only focus on mount — the listbox is unmounted/remounted each time the
    // popover opens, so this effectively runs once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focusOption = (index: number) => {
    setActiveIndex(index);
    optionRefs.current[index]?.focus();
  };

  const handleSelect = (option: SortOption) => {
    if (!option.selected) onSelect(option.name);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const lastIndex = options.length - 1;

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        focusOption(activeIndex === lastIndex ? 0 : activeIndex + 1);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        focusOption(activeIndex === 0 ? lastIndex : activeIndex - 1);
        break;
      }
      case 'Home': {
        event.preventDefault();
        focusOption(0);
        break;
      }
      case 'End': {
        event.preventDefault();
        focusOption(lastIndex);
        break;
      }
      case ' ':
      case 'Enter': {
        event.preventDefault();
        optionRefs.current[activeIndex]?.click();
        break;
      }
    }
  };

  return (
    <Styled.Listbox
      role="listbox"
      aria-label="Sort options"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {options.map((option, index) => (
        <Styled.Option
          key={option.name || 'best-match'}
          ref={(node) => {
            optionRefs.current[index] = node;
          }}
          role="option"
          aria-selected={option.selected}
          tabIndex={index === activeIndex ? 0 : -1}
          $selected={option.selected}
          onClick={() => handleSelect(option)}
        >
          {option.displayName}
        </Styled.Option>
      ))}
    </Styled.Listbox>
  );
};

export { SortOptionsListbox };
export type { SortOption };
